<template>
  <label
    class="RadioButtonComponent"
    :for="`${id}-${value}`"
    @keydown.enter.prevent="$emit('keydown', $event)"
    @keydown.space.prevent="$emit('keydown', $event)"
  >
    <div
      class="card"
      :class="{ selected: selected, small: small, disabled: !!disabled }"
    >
      <input
        type="radio"
        :name="`radio-input-${id}`"
        :value="value"
        :id="`${id}-${value}`"
        :checked="selected"
        :disabled="!!disabled"
        :tabindex="disabled ? -1 : isTabbable ? 0 : -1"
        :aria-checked="selected ? 'true' : 'false'"
        @change="$emit('change', $event)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @keydown="$emit('keydown', $event)"
      />

      <span class="input"></span>

      <span v-html="label"></span>
    </div>
  </label>
</template>

<script setup lang="ts">
  defineProps({
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    isTabbable: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  })
  defineEmits(['change', 'focus', 'blur', 'keydown'])
</script>

<style lang="scss" scoped>
  .RadioButtonComponent {
    .card {
      cursor: pointer;
      display: flex;
      padding: $spacing-xl;
      align-items: center;
      gap: $gap-10;
      border-radius: $b-radius-2;
      border: solid 1px $color-neutral-light;
      background: $color-neutral-lighter;
      font-family: $font-family-primary;
      font-size: $font-size-l;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-140p;
      color: $color-neutral-dark;

      &.selected {
        color: $color-primary;
        border-color: $color-primary;
        background-color: $color-primary-lighter;
      }

      .input {
        height: $size-16;
        align-self: center;
        background-color: $color-neutral-00;
        border-radius: $b-radius-50;
        aspect-ratio: 1;
        transition: box-shadow 0.1s ease-in;
        box-shadow: inset 0 0 0 1px $color-neutral;
      }

      &.disabled {
        border-color: $color-grey-200;
        color: $color-neutral-light;
        cursor: default;

        .input {
          box-shadow: inset 0 0 0 1px $color-neutral-light;
        }
      }

      input {
        all: unset;
        width: 0;
        height: 0;
        position: absolute;

        &:checked + .input {
          background-color: $color-neutral-00;
          box-shadow: inset 0 0 0 4px $color-primary;
        }

        &:focus + .input {
          outline: 2px solid $color-primary;
          outline-offset: 2px;
        }

        &:focus-visible + .input {
          box-shadow: 0 0 0 4px $color-focus;
          outline: none;
        }
      }
    }

    &:focus-within .card {
      box-shadow: $box-shadow-focus;
      outline: none;
    }
  }
</style>
